import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { TicketsConfigApiService } from '../../../../tickets/services/tickets-config-api.service';
import { ContactCycleApiService } from '../../../../contact-cycles/services/contact-cycle-api.service';
import { ContactStateApiService } from '../../../../contact-states/services/contact-state-api.service';
import { ContactOriginApiService } from '../../../../contact-origins/services/contact-origin-api.service';
import { DealCycleApiService } from '../../../../deal-cycles/services/deal-cycle-api.service';
import { DealOriginApiService } from '../../../../deal-origins/services/deal-origin-api.service';
import { DealPriorityApiService } from '../../../../deal-priorities/services/deal-priority-api.service';
import { DealBusinessTypeApiService } from '../../../../deal-business-types/services/deal-business-type-api.service';
import { QuoteStatusApiService } from '../../../../quotes/services/quote-status-api.service';
import { CompanySectorApiService } from '../../../../empresas/services/company-sector-api.service';
import { CompanySizeApiService } from '../../../../empresas/services/company-size-api.service';
import { CompanyCycleApiService } from '../../../../empresas/services/company-cycle-api.service';
import { CompanyStateLeadApiService } from '../../../../empresas/services/company-state-lead-api.service';
import { CompanyRelationshipTypeApiService } from '../../../../empresas/services/company-relationship-type-api.service';
import { CompanyTaxTypeApiService } from '../../../../empresas/services/company-tax-type-api.service';
import { ProductoApiService } from '../../../../productos/services/producto-api.service';
import { AuthService } from '../../../../auth-pages/services/auth.service';
import { WorkflowValueCatalogKey } from '../models/workflow-variable-catalog';

/** Un valor posible de una propiedad de catálogo: lo que se guarda + lo que se ve. */
export interface WorkflowCatalogValue {
  /** Id del elemento del catálogo (lo que se compara cuando la propiedad es `idXxx`). */
  id: number | null;
  /** Nombre del elemento (lo que se compara cuando la propiedad es `xxxName`). */
  name: string;
  /** Texto que ve el usuario en el desplegable. */
  label: string;
}

/**
 * Valores disponibles de las propiedades de CATÁLOGO del constructor de workflows.
 *
 * INC_CRM_020 (9) inc49/50/52/53/55 — los campos de comparación («Desde», «Hasta», el valor de una
 * condición o de un filtro) eran SIEMPRE un input de texto: para acotar por estado, prioridad,
 * categoría o etapa había que conocer y teclear el valor exacto, y escribirlo mal daba una
 * condición que evaluaba falso para siempre sin ningún aviso. Este servicio es la fuente de las
 * opciones de esos desplegables.
 *
 * Cada catálogo se pide UNA vez por sesión del builder (`shareReplay`) porque son listas cortas
 * del negocio que no cambian mientras se edita un flujo, y un fallo de red devuelve lista vacía:
 * el editor cae entonces al texto libre en vez de dejar el campo inservible.
 */
@Injectable({ providedIn: 'root' })
export class WorkflowValueCatalogService {
  private readonly ticketsConfig = inject(TicketsConfigApiService);
  private readonly contactCycleApi = inject(ContactCycleApiService);
  private readonly contactStateApi = inject(ContactStateApiService);
  private readonly contactOriginApi = inject(ContactOriginApiService);
  private readonly dealCycleApi = inject(DealCycleApiService);
  private readonly dealOriginApi = inject(DealOriginApiService);
  private readonly dealPriorityApi = inject(DealPriorityApiService);
  private readonly dealBusinessTypeApi = inject(DealBusinessTypeApiService);
  private readonly quoteStatusApi = inject(QuoteStatusApiService);
  private readonly companySectorApi = inject(CompanySectorApiService);
  private readonly companySizeApi = inject(CompanySizeApiService);
  private readonly companyCycleApi = inject(CompanyCycleApiService);
  private readonly companyStateApi = inject(CompanyStateLeadApiService);
  private readonly companyRelationshipApi = inject(CompanyRelationshipTypeApiService);
  private readonly companyTaxTypeApi = inject(CompanyTaxTypeApiService);
  private readonly productoApi = inject(ProductoApiService);
  private readonly auth = inject(AuthService);

  private readonly cache = new Map<WorkflowValueCatalogKey, Observable<WorkflowCatalogValue[]>>();

  /**
   * Orígenes de un ticket. Es un enum del contrato (no una tabla), así que vive aquí con su
   * etiqueta en español: el valor guardado es la CLAVE, que es lo que compara el motor.
   */
  private static readonly TICKET_SOURCES: WorkflowCatalogValue[] = [
    { id: null, name: 'MANUAL', label: 'Manual' },
    { id: null, name: 'EMAIL', label: 'Correo electrónico' },
    { id: null, name: 'CHAT', label: 'Chat' },
    { id: null, name: 'PHONE', label: 'Teléfono' },
    { id: null, name: 'WEB_FORM', label: 'Formulario web' },
    { id: null, name: 'PORTAL', label: 'Portal del cliente' },
    { id: null, name: 'SOCIAL', label: 'Redes sociales' },
    { id: null, name: 'API', label: 'API' }
  ];

  values(catalog: WorkflowValueCatalogKey): Observable<WorkflowCatalogValue[]> {
    const cached = this.cache.get(catalog);
    if (cached) {
      return cached;
    }
    const request$ = this.load(catalog).pipe(
      catchError(() => of([] as WorkflowCatalogValue[])),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.cache.set(catalog, request$);
    return request$;
  }

  private load(catalog: WorkflowValueCatalogKey): Observable<WorkflowCatalogValue[]> {
    const businessId = this.auth.getCurrentUser()?.businessId || 1;
    switch (catalog) {
      case 'TICKET_STATUS':
        return this.ticketsConfig
          .getStatuses()
          .pipe(map((list) => (list ?? []).map((s) => ({ id: s.id, name: s.name, label: s.label || s.name }))));
      case 'TICKET_PRIORITY':
        return this.ticketsConfig
          .getPriorities()
          .pipe(map((list) => (list ?? []).map((p) => ({ id: p.id, name: p.name, label: p.label || p.name }))));
      case 'TICKET_CATEGORY':
        return this.ticketsConfig
          .getCategories()
          .pipe(map((list) => (list ?? []).map((c) => ({ id: c.id, name: c.name, label: c.name }))));
      case 'TICKET_PIPELINE':
        return this.ticketsConfig
          .getPipelines()
          .pipe(map((list) => (list ?? []).map((p) => ({ id: p.id, name: p.name, label: p.name }))));
      // Las etapas cuelgan de un pipeline y no hay endpoint que las liste todas, así que se pide
      // una vez por pipeline y se aplanan con el pipeline en la etiqueta: dos pipelines pueden
      // tener una etapa "Abierto" cada uno y el id guardado es distinto.
      case 'TICKET_STAGE':
        return this.ticketsConfig.getPipelines().pipe(
          switchMap((pipelines) => {
            const list = pipelines ?? [];
            if (list.length === 0) {
              return of([] as WorkflowCatalogValue[]);
            }
            return forkJoin(
              list.map((pipeline) =>
                this.ticketsConfig.getPipelineStages(pipeline.id).pipe(
                  catchError(() => of([])),
                  map((stages) =>
                    (stages ?? []).map((stage) => ({
                      id: stage.id,
                      name: stage.name,
                      label: `${stage.name} — ${pipeline.name}`
                    }))
                  )
                )
              )
            ).pipe(map((groups) => groups.flat()));
          })
        );
      case 'CONTACT_CYCLE':
        return this.contactCycleApi
          .getCycles(businessId, 0, 200)
          .pipe(map((list) => (list ?? []).map((c) => ({ id: c.id, name: c.name, label: c.name }))));
      case 'CONTACT_STATE':
        return this.contactStateApi
          .getStates(businessId, 0, 200)
          .pipe(map((list) => (list ?? []).map((s) => ({ id: s.id, name: s.name, label: s.name }))));
      case 'DEAL_STAGE':
        return this.dealCycleApi
          .getAll()
          .pipe(map((list) => (list ?? []).map((c) => ({ id: c.id, name: c.name, label: c.name }))));
      case 'CONTACT_ORIGIN':
        return this.contactOriginApi.getOrigins(businessId, 0, 200).pipe(map(named));
      case 'DEAL_ORIGIN':
        return this.dealOriginApi.getAll().pipe(map(named));
      case 'DEAL_PRIORITY':
        return this.dealPriorityApi.getAll(businessId, 0, 200).pipe(map((page) => named(page?.content)));
      case 'DEAL_BUSINESS_TYPE':
        return this.dealBusinessTypeApi.getAll(businessId, 0, 200).pipe(map((page) => named(page?.content)));
      case 'QUOTE_STATUS':
        return this.quoteStatusApi.getAllStatuses().pipe(map(named));
      case 'COMPANY_SECTOR':
        return this.companySectorApi.getAll().pipe(map(named));
      case 'COMPANY_SIZE':
        return this.companySizeApi.getAll({ page: 0, size: 200 }).pipe(map((page) => named(page?.content)));
      case 'COMPANY_CYCLE':
        return this.companyCycleApi
          .getAll(businessId, { page: 0, size: 200 })
          .pipe(map((page) => named(page?.content)));
      case 'COMPANY_STATE':
        return this.companyStateApi.getAll({ page: 0, size: 200 }).pipe(map((page) => named(page?.content)));
      case 'COMPANY_RELATIONSHIP_TYPE':
        return this.companyRelationshipApi.getAll({ page: 0, size: 200 }).pipe(map((page) => named(page?.content)));
      case 'COMPANY_TAX_TYPE':
        return this.companyTaxTypeApi.getAll({ page: 0, size: 200 }).pipe(map((page) => named(page?.content)));
      // La moneda se guarda por id; se muestra con el código, que es como la reconoce el usuario.
      case 'CURRENCY':
        return this.productoApi.getCurrencies().pipe(
          map((list) =>
            (list ?? []).map((c) => ({ id: c.id, name: c.code || c.name, label: `${c.code} — ${c.name}` }))
          )
        );
      case 'TICKET_SOURCE':
        return of(WorkflowValueCatalogService.TICKET_SOURCES);
      // `ticket.sourceLabel` guarda el origen ya traducido, no la clave: el valor que se compara
      // ES la etiqueta, y tiene que coincidir al carácter con `origenLegible()` del backend.
      case 'TICKET_SOURCE_LABEL':
        return of(
          WorkflowValueCatalogService.TICKET_SOURCES.map((s) => ({ id: null, name: s.label, label: s.label }))
        );
      default:
        return of([]);
    }
  }
}

/**
 * Todos los catálogos del CRM responden con la misma forma `{id, name}` —comprobado uno a uno en
 * sus modelos—, así que el mapeo a opción de desplegable es el mismo para los doce.
 */
function named(list: readonly { id: number; name: string }[] | null | undefined): WorkflowCatalogValue[] {
  return (list ?? []).map((item) => ({ id: item.id, name: item.name, label: item.name }));
}
